import { formatCents } from "@/lib/money";

import {
  BarList,
  ReportSection,
  ReportTable,
  Stat,
  StatGrid,
} from "../ReportPrimitives";
import { AGING_LABELS, type FinanceReport } from "../reportTables";

const pretty = (value: string) =>
  value.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

/**
 * Finance: the money, including what is late and by how long. The aging
 * buckets are the standard AR view; an invoice with no due date counts as
 * current rather than being reported as late.
 */
export const FinancePanel = ({ data }: { data: FinanceReport }) => {
  const acceptRate =
    data.estimates.sent + data.estimates.accepted + data.estimates.declined ===
    0
      ? "n/a"
      : `${Math.round(
          (data.estimates.accepted /
            (data.estimates.sent +
              data.estimates.accepted +
              data.estimates.declined)) *
            100,
        )}%`;

  return (
    <div className="flex flex-col gap-4">
      <StatGrid>
        <Stat
          label="Invoiced"
          value={formatCents(data.invoiced_cents)}
          helper="Issued in this period"
        />
        <Stat
          label="Collected"
          value={formatCents(data.collected_cents)}
          helper="Payments received"
          tone="positive"
        />
        <Stat
          label="Outstanding"
          value={formatCents(data.outstanding_cents)}
          helper="Owed right now"
          tone={data.outstanding_cents > 0 ? "negative" : "default"}
        />
        <Stat
          label="Refunded"
          value={formatCents(data.refunded_cents)}
          helper={`${formatCents(data.written_off_cents)} written off`}
        />
      </StatGrid>

      <div className="grid gap-4 lg:grid-cols-2 [&>*]:min-w-0">
        <ReportSection
          title="Receivables aging"
          subtitle="Unpaid balances by how late they are"
        >
          <BarList
            items={AGING_LABELS.map((bucket) => ({
              label: bucket.label,
              value: data.aging[bucket.key],
              display: formatCents(data.aging[bucket.key]),
            })).filter((item) => item.value > 0)}
            empty="Nothing outstanding."
          />
        </ReportSection>

        <ReportSection title="Invoices by status" subtitle="Whole workspace">
          <ReportTable
            columns={["Status", "Invoices", "Value"]}
            align={["left", "right", "right"]}
            rows={data.by_status.map((row) => [
              pretty(row.status),
              row.count,
              formatCents(row.cents),
            ])}
            empty="No invoices yet."
          />
        </ReportSection>
      </div>

      <div className="grid gap-4 lg:grid-cols-2 [&>*]:min-w-0">
        <ReportSection title="Payments by method" subtitle="In this period">
          <ReportTable
            columns={["Method", "Count", "Value"]}
            align={["left", "right", "right"]}
            rows={data.by_method.map((row) => [
              pretty(row.method),
              row.count,
              formatCents(row.cents),
            ])}
            empty="No payments in this period."
          />
        </ReportSection>

        <ReportSection
          title="Top customers"
          subtitle="By payments received in this period"
        >
          <BarList
            items={data.top_customers.map((row) => ({
              label: row.name,
              value: row.cents,
              display: formatCents(row.cents),
            }))}
            empty="No payments in this period."
          />
        </ReportSection>
      </div>

      <ReportSection title="Estimates" subtitle="Created in this period">
        <StatGrid>
          <Stat label="Sent" value={String(data.estimates.sent)} />
          <Stat
            label="Accepted"
            value={String(data.estimates.accepted)}
            helper={formatCents(data.estimates.accepted_cents)}
            tone="positive"
          />
          <Stat label="Declined" value={String(data.estimates.declined)} />
          <Stat label="Accept rate" value={acceptRate} />
        </StatGrid>
      </ReportSection>
    </div>
  );
};
